<template>
  <div
    :data-testid="`footer-group-${title?.toLowerCase().replace(/\s+/g, '-')}`"
  >
    <!-- The heading wraps the toggle rather than sitting inside it: ARIA treats
         a button's descendants as presentational, so a role on the label would
         be dropped from the accessibility tree. -->
    <div class="md:hidden" role="heading" aria-level="2">
      <button
        type="button"
        class="block relative w-full py-4 no-underline cursor-pointer text-center"
        :aria-expanded="!isCollapsed"
        @click="toggleCollapse()"
      >
        <span class="block text-base mb-0 font-bold uppercase text-mouse">{{
          title
        }}</span>
        <fa-icon
          class="size-4 absolute top-5 right-4 block text-rhino transition-transform duration-300"
          :style="{
            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
          }"
          :icon="faChevronDown"
        />
      </button>
    </div>

    <p
      class="text-base hidden md:block mb-2 font-bold uppercase text-mouse"
      role="heading"
      aria-level="2"
    >
      {{ title }}
    </p>

    <transition name="collapse">
      <div v-show="!isCollapsed" class="md:block md:pb-0">
        <ul class="mb-4 md:mb-0 md:mt-4 text-center md:text-left">
          <li
            v-for="link in links"
            :key="
              link.data.url != null
                ? `${link.data.url}__${link.key}`
                : `${link.label}__${link.key}`
            "
            class="leading-5 md:leading-7"
          >
            <footer-link-anchor :footer-link="link" />
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import type { FooterLink } from './types';

defineProps({
  title: { type: String, default: 'Panel Title' },
  links: { type: Array as PropType<FooterLink[]>, default: () => [] },
});

const isCollapsed = ref(true);
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};
</script>

<style scoped>
@media (min-width: 768px) {
  .md\:block {
    display: block !important;
    max-height: none !important;
    opacity: 1 !important;
    overflow: visible !important;
  }
}

.collapse-enter-active,
.collapse-leave-active {
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 20rem; /* Sufficient for link groups */
  opacity: 1;
}
</style>
